// ══════════════════════════════════════════════════════
//  Ajustar raciones: multiplica la cantidad con la que EMPIEZA cada línea de ingredientes.
//  Los ingredientes se guardan como texto libre («2 huevos», «½ cebolla», «250 g de harina»),
//  así que se lee solo el número inicial. Una línea sin cantidad al principio («sal»,
//  «aceite de oliva») se deja como está. Con formato (HTML en línea) se salta la apertura de
//  etiquetas y se escala el texto que viene después.
// ══════════════════════════════════════════════════════

const FRACCIONES = { "½": 0.5, "⅓": 1 / 3, "⅔": 2 / 3, "¼": 0.25, "¾": 0.75, "⅛": 0.125 };
const CARACTERES = Object.keys(FRACCIONES).join("");
// Una cantidad: «1 1/2», «3/4», «1½», «½», «2,5» o «2»
const CANTIDAD = String.raw`(?:\d+\s+\d+/\d+|\d+/\d+|\d*\s?[${CARACTERES}]|\d+(?:[.,]\d+)?)`;
// Cantidad sola o rango («2-3», «2 a 3»); no puede seguir otro dígito ni «/» ni «%» ni «º»
const INICIO = new RegExp(String.raw`^(${CANTIDAD})(?:(\s*(?:-|–|a)\s*)(${CANTIDAD}))?(?![\d/%º°:])`);

const aNumero = (t) => {
  const s = t.trim();
  let m = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return Number(m[2]) ? Number(m[1]) / Number(m[2]) : NaN;
  m = s.match(new RegExp(String.raw`^(\d*)\s?([${CARACTERES}])$`));
  if (m) return Number(m[1] || 0) + FRACCIONES[m[2]];
  return Number(s.replace(",", "."));
};

const UNICODE = [
  [0.5, "½"],
  [0.25, "¼"],
  [0.75, "¾"],
  [1 / 3, "⅓"],
  [2 / 3, "⅔"],
];

// Número → texto legible: enteros, fracciones comunes (1 ½) o decimales con coma
export const formatear = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 100) return String(Math.round(n));
  if (Math.abs(n - Math.round(n)) < 0.03) return String(Math.round(n));
  const parte = n - Math.floor(n);
  const f = UNICODE.find(([v]) => Math.abs(parte - v) < 0.03);
  if (f) return Math.floor(n) > 0 ? `${Math.floor(n)} ${f[1]}` : f[1];
  const dec = n < 10 ? Math.round(n * 10) / 10 : Math.round(n);
  return String(dec).replace(".", ",");
};

// Concordancia de la palabra que sigue a la cantidad: «1 huevo» → «2 huevos», «2 limones» → «1 limón».
// Solo toca palabras sueltas de 4+ letras; las unidades cortas (g, kg, ml…) quedan igual.
const PALABRA = /^(\s+)([A-Za-zÁÉÍÓÚÜáéíóúüñÑ]{4,})(?![A-Za-zÁÉÍÓÚÜáéíóúüñÑ])/;
const QUITAR_TILDE = { á: "a", é: "e", í: "i", ó: "o", ú: "u" };
const aPlural = (p) => {
  if (/s$/i.test(p)) return p;
  const m = p.match(/^(.*)([áéíóú])n$/i);
  if (m) return `${m[1]}${QUITAR_TILDE[m[2].toLowerCase()]}nes`;
  return /[aeiouáéíóú]$/i.test(p) ? `${p}s` : `${p}es`;
};
const aSingular = (p) => {
  if (!/s$/i.test(p)) return p;
  if (/(?:[nldrz])es$/i.test(p)) return p.slice(0, -2);
  return p.slice(0, -1);
};
const concordar = (resto, antes, ahora) => {
  const m = resto.match(PALABRA);
  if (!m) return resto;
  let palabra = m[2];
  if (antes <= 1.03 && ahora > 1.03) palabra = aPlural(palabra);
  else if (antes > 1.03 && ahora <= 1.03) palabra = aSingular(palabra);
  return m[1] + palabra + resto.slice(m[0].length);
};

// Escala una línea de ingredientes. Devuelve { texto, escalada }.
export const escalarLinea = (linea, factor) => {
  if (!factor || factor === 1) return { texto: linea, escalada: false };
  const [, etiquetas, resto] = String(linea).match(/^((?:<[^<>]*>)*)([\s\S]*)$/);
  const m = resto.match(INICIO);
  if (!m) return { texto: linea, escalada: false };
  const a = aNumero(m[1]);
  const b = m[3] ? aNumero(m[3]) : null;
  if (!Number.isFinite(a) || a <= 0 || (b !== null && (!Number.isFinite(b) || b <= 0))) return { texto: linea, escalada: false };
  const nuevo = b === null ? formatear(a * factor) : `${formatear(a * factor)}${m[2]}${formatear(b * factor)}`;
  let cola = resto.slice(m[0].length);
  // Solo con una cantidad suelta (no rangos ni fracciones): «1» ↔ «2 o más»
  if (b === null) cola = concordar(cola, a, a * factor);
  return { texto: etiquetas + nuevo + cola, escalada: true };
};

// Escala las cantidades de un paso: las que van seguidas de una unidad de medida («300 g», «½ cucharadita»)
// o de un ingrediente («1 huevo», «2 cebollas»), igual que la lista de ingredientes. Se dejan tiempos,
// temperaturas, tamaños, «veces» y palabras de enlace («8–10 minutos», «180 °C», «2 cm», «3 veces»).
// El HTML en línea se respeta: solo se toca el texto.
const UNIDADES =
  "kg|mg|gr|g|ml|cl|dl|l|litros?|gramos?|kilos?|cucharadas?|cucharaditas?|cucharones?|cucharón|vasos?|tazas?|pizcas?|cdas?|cdtas?";
const NO_ES_CANTIDAD =
  "minutos?|min|segundos?|seg|horas?|h|d[ií]as?|semanas?|veces|vez|cm|mm|m|cent[ií]metros?|mil[ií]metros?|metros?|grados?|pasos?|dedos?|partes?|capas?|lados?|bolitas?|trozos?|porciones?|piezas?|unidades?|de|del|en|con|y|o|u|a|al|la|el|los|las|un|una|unos|unas|por|para|se|que|más|menos|veces";
const MEDIDA = new RegExp(
  String.raw`(?<![\d.,/\p{L}])(${CANTIDAD})(?:(\s*(?:-|–|a)\s*)(${CANTIDAD}))?(?:(\s*)(${UNIDADES})(?![\p{L}\d])|(\s+)(?!(?:${NO_ES_CANTIDAD})(?![\p{L}\d]))(\p{L}{3,}))`,
  "giu",
);

export const escalarPaso = (paso, factor) => {
  if (!factor || factor === 1 || typeof paso !== "string") return paso;
  return paso
    .split(/(<[^<>]*>)/)
    .map((trozo) => {
      if (trozo.startsWith("<")) return trozo;
      return trozo.replace(MEDIDA, (todo, n1, union, n2, espacioU, unidadU, espacioP, palabraP) => {
        const espacio = espacioU ?? espacioP;
        const unidad = unidadU ?? palabraP;
        const a = aNumero(n1);
        const b = n2 ? aNumero(n2) : null;
        if (!Number.isFinite(a) || a <= 0 || (b !== null && (!Number.isFinite(b) || b <= 0))) return todo;
        const nuevo = b === null ? formatear(a * factor) : `${formatear(a * factor)}${union}${formatear(b * factor)}`;
        // Concordancia solo con cantidad suelta: «1 cucharada» ↔ «2 cucharadas»
        const palabra = b === null ? concordar(` ${unidad}`, a, a * factor).slice(1) : unidad;
        return `${nuevo}${espacio}${palabra}`;
      });
    })
    .join("");
};

// ¿Tiene alguna línea una cantidad que se pueda ajustar?
export const hayCantidades = (lineas) => lineas.some((l) => escalarLinea(l, 2).escalada);

// Cantidad con la que empieza una línea, para poder SUMAR la misma línea de varias recetas
// (lista de la compra). Devuelve { valor, resto } o null si no empieza por una cantidad suelta
// (sin cantidad o con rango: esas no se suman).
export const cantidadInicial = (linea) => {
  const resto = String(linea).trim();
  const m = resto.match(INICIO);
  if (!m || m[3]) return null;
  const valor = aNumero(m[1]);
  return Number.isFinite(valor) && valor > 0 ? { valor, resto: resto.slice(m[0].length) } : null;
};
